/**
 * DebateTranscript Component
 *
 * Displays the round-by-round debate history.
 */

import { useState } from 'react';
import { clsx } from 'clsx';
import { MessageSquare, ChevronRight } from 'lucide-react';
import { ContributionCard } from './ContributionCard';
import type { DebateRound, ContributionType, LLMProvider } from '../../../lib/api';

interface DebateTranscriptProps {
  rounds: DebateRound[];
  defaultExpandedRound?: number;
}

const ROUND_TYPE_LABELS: Record<string, { label: string; description: string }> = {
  INITIAL: {
    label: 'Initial Proposals',
    description: 'Each expert presents their initial assessment and recommendations',
  },
  PEER_REVIEW: {
    label: 'Peer Review',
    description: 'Experts review and critique each other\'s proposals',
  },
  RESPONSE: {
    label: 'Response & Defense',
    description: 'Experts respond to critiques and defend or adjust their positions',
  },
  CLARIFICATION: {
    label: 'Clarification',
    description: 'Additional clarification and position refinement',
  },
  MODERATOR_SYNTHESIS: {
    label: 'Moderator Synthesis',
    description: 'The Chief of Medicine synthesizes the debate into final recommendations',
  },
};

export function DebateTranscript({ rounds, defaultExpandedRound }: DebateTranscriptProps) {
  const [expandedRound, setExpandedRound] = useState<number | null>(
    defaultExpandedRound ?? (rounds.length > 0 ? rounds[rounds.length - 1].roundNumber : null)
  );

  const sortedRounds = [...rounds].sort((a, b) => a.roundNumber - b.roundNumber);

  if (rounds.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No debate rounds yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sortedRounds.map((round) => {
        const isExpanded = expandedRound === round.roundNumber;
        const typeConfig = ROUND_TYPE_LABELS[round.roundType] || {
          label: round.roundType,
          description: '',
        };

        return (
          <div key={round.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Round Header */}
            <button
              className={clsx(
                'w-full flex items-center justify-between p-4 text-left transition-colors',
                isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'
              )}
              onClick={() => setExpandedRound(isExpanded ? null : round.roundNumber)}
            >
              <div className="flex items-center gap-3">
                <div
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold',
                    round.status === 'COMPLETED'
                      ? 'bg-green-100 text-green-700'
                      : round.status === 'RUNNING'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {round.roundNumber}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{typeConfig.label}</h3>
                  <p className="text-xs text-gray-500">{typeConfig.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {round.contributions && (
                  <span className="text-sm text-gray-500">
                    {round.contributions.length} contribution{round.contributions.length !== 1 ? 's' : ''}
                  </span>
                )}
                <ChevronRight
                  className={clsx(
                    'h-5 w-5 text-gray-400 transition-transform',
                    isExpanded && 'rotate-90'
                  )}
                />
              </div>
            </button>

            {/* Round Content */}
            {isExpanded && round.contributions && (
              <div className="border-t border-gray-200 p-4 space-y-3 bg-gray-50">
                {round.contributions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No contributions in this round yet
                  </p>
                ) : (
                  round.contributions.map((contribution) => (
                    <ContributionCard
                      key={contribution.id}
                      model={contribution.model as LLMProvider}
                      persona={contribution.persona}
                      isModeratorAgent={contribution.isModeratorAgent}
                      contributionType={contribution.contributionType as ContributionType}
                      content={contribution.content as Record<string, unknown>}
                      confidence={contribution.confidence}
                      latencyMs={contribution.latencyMs}
                      tokenUsage={contribution.tokenUsage as { input: number; output: number } | null}
                      defaultExpanded={round.roundType === 'MODERATOR_SYNTHESIS'}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
